/**
 * Export straight to SharePoint/OneDrive, Google Drive, Dropbox and Nucleus One
 * from this PC (docs/planning/desktop-cloud-exports.md). The shapes the engine
 * answers with, and the few words the destination screen uses for each provider.
 * Tokens and API keys never reach the renderer: it sees accounts by name and
 * state only, and a Nucleus One key is typed into Paige's protected key
 * window, never into the page.
 */

/** The cloud-export IPC channels (docs/planning/desktop-cloud-exports.md). */
export const cloudExportChannels = {
  providers: 'cloud-export:providers',
  signIn: 'cloud-export:sign-in',
  signInStatus: 'cloud-export:sign-in-status',
  cancelSignIn: 'cloud-export:cancel-sign-in',
  signOut: 'cloud-export:sign-out',
  browse: 'cloud-export:browse',
  newFolder: 'cloud-export:new-folder',
  destination: 'cloud-export:destination',
  saveDestination: 'cloud-export:save-destination',
  removeDestination: 'cloud-export:remove-destination',
  test: 'cloud-export:test',
  sendEarlier: 'cloud-export:send-earlier',
  sendUnsent: 'cloud-export:send-unsent',
  retry: 'cloud-export:retry',
  connectKey: 'cloud-export:connect-key',
  fields: 'cloud-export:fields',
  resendUnconfirmed: 'cloud-export:resend-unconfirmed',
} as const;

export const CLOUD_PROVIDER_KINDS = [
  'microsoft-365',
  'google-drive',
  'dropbox',
  'nucleus-one',
] as const;
export type CloudProviderKind = (typeof CLOUD_PROVIDER_KINDS)[number];

/** The providers reached with an API key typed into the protected key window, not a browser sign-in. */
export const CLOUD_API_KEY_KINDS: readonly CloudProviderKind[] = ['nucleus-one'];
export const usesApiKey = (kind: CloudProviderKind | null | undefined) =>
  Boolean(kind && CLOUD_API_KEY_KINDS.includes(kind));

/** One line of a repository's field map: the job's field and the repository's field ("" leaves it out). */
export type CloudFieldMapping = { field: string; target: string };

/** A repository field (Nucleus One's project fields), for the field map's suggestions. */
export type CloudRepositoryField = { name: string; type: string };

export type CloudProviderSummary = { kind: CloudProviderKind; product: string; available: boolean };

export type CloudAccountSummary = {
  accountId: string;
  kind: CloudProviderKind;
  name: string;
  state: 'connected' | 'reconnect';
  connectedAt: string;
};

export type CloudProvidersAndAccounts = {
  providers: CloudProviderSummary[];
  accounts: CloudAccountSummary[];
};

export type CloudSignInStatus = {
  signInId: string;
  kind: CloudProviderKind | '';
  state: 'waiting' | 'connected' | 'failed' | 'canceled';
  account: CloudAccountSummary | null;
  message: string | null;
};

export type CloudPlace = {
  id: string;
  name: string;
  kind: 'site' | 'library' | 'drive' | 'folder';
  selectable: boolean;
};

export type CloudDestinationRecord = {
  destinationId: string;
  jobId: string;
  accountId: string;
  kind: CloudProviderKind;
  locationId: string;
  locationPath: string;
  sendValues: boolean;
  enabled: boolean;
  sendFromUtc: string | null;
  fieldMap?: CloudFieldMapping[] | null;
};

export type CloudDeliverySummary = {
  exportId: string;
  documentLabel: string;
  state: 'waiting' | 'sent' | 'failed';
  place: string | null;
  fileName: string | null;
  webUrl: string | null;
  message: string | null;
  updatedUtc: string;
};

export type CloudDestinationSummary = {
  destination: CloudDestinationRecord | null;
  account: CloudAccountSummary | null;
  counts: { sent: number; waiting: number; failed: number; unconfirmed?: number };
  recent: CloudDeliverySummary[];
  earlierExports: number;
  /** Documents an earlier destination of this job left unsent when it was switched or removed. */
  unsent?: number;
  unsentFrom?: string | null;
};

/** What the screen says about documents an earlier destination left unsent. */
export function cloudUnsentLine(count: number, from: string | null | undefined): string | null {
  if (count <= 0) return null;
  const where = from ?? 'the previous destination';
  return count === 1
    ? `1 document for ${where} was not sent.`
    : `${count} documents for ${where} were not sent.`;
}

/** The warning before switching or stopping a destination that still has documents waiting. */
export function cloudLeaveWarning(waiting: number, product: string): string {
  return waiting === 1
    ? `1 document waiting for ${product} won't be sent there.`
    : `${waiting} documents waiting for ${product} won't be sent there.`;
}

export type CloudDestinationInput = {
  accountId: string;
  locationId: string;
  locationPath: string;
  sendValues: boolean;
  enabled: boolean;
  fieldMap?: CloudFieldMapping[];
};

export type CloudTestResult = {
  succeeded: boolean;
  message: string;
  place: string | null;
  fields?: CloudRepositoryField[] | null;
};

/** What the screen says about documents that may already be in a repository (the filing call had no answer). */
export function cloudUnconfirmedLine(count: number, product: string): string | null {
  if (count <= 0) return null;
  return count === 1
    ? `1 document may already be in ${product}: Paige could not tell. Look for it before sending it again.`
    : `${count} documents may already be in ${product}: Paige could not tell. Look for them before sending them again.`;
}

/** Per-provider words: the brand people know, what a "place" is called there, and where values go. */
export const CLOUD_PROVIDER_WORDS: Record<
  CloudProviderKind,
  {
    title: string;
    short: string;
    pick: string;
    pickTitle: string;
    search: string;
    values: string | null;
  }
> = {
  'microsoft-365': {
    title: 'SharePoint or OneDrive',
    short: 'Microsoft 365',
    pick: 'Choose a library or folder',
    pickTitle: 'Choose a SharePoint library or OneDrive folder',
    search: 'Find a SharePoint site',
    values: 'Also fill SharePoint columns with the same names',
  },
  'google-drive': {
    title: 'Google Drive',
    short: 'Google Drive',
    pick: 'Choose a Drive folder',
    pickTitle: 'Choose a Google Drive folder',
    search: 'Find a folder or shared drive',
    values: "Also add the values to each file's Drive details",
  },
  dropbox: {
    title: 'Dropbox',
    short: 'Dropbox',
    pick: 'Choose a Dropbox folder',
    pickTitle: 'Choose a Dropbox folder',
    search: 'Find a folder',
    values: null,
  },
  'nucleus-one': {
    title: 'Nucleus One',
    short: 'Nucleus One',
    pick: 'Choose a project or folder',
    pickTitle: 'Choose a Nucleus One project or folder',
    search: 'Find an organization',
    values: null,
  },
};

/** The progress line under a cloud destination: "12 sent · 2 waiting · 1 needs attention". */
export function cloudDeliveryLine(counts: {
  sent: number;
  waiting: number;
  failed: number;
  unconfirmed?: number;
}): string {
  const parts: string[] = [];
  if (counts.sent > 0) parts.push(`${counts.sent} sent`);
  if (counts.waiting > 0) parts.push(`${counts.waiting} waiting`);
  if (counts.failed > 0)
    parts.push(`${counts.failed} need${counts.failed === 1 ? 's' : ''} attention`);
  if ((counts.unconfirmed ?? 0) > 0) parts.push(`${counts.unconfirmed} to check`);
  return parts.length === 0 ? 'Nothing sent yet' : parts.join(' · ');
}

/** The offer to send what the job exported before this destination: "Send the 38 documents exported before". */
export function cloudEarlierOffer(count: number): string | null {
  if (count <= 0) return null;
  return count === 1
    ? 'Send the 1 document exported before'
    : `Send the ${count} documents exported before`;
}

const ACCOUNT_ID = /^acct_[0-9a-f]{32}$/;
const FIELD_MAP_MAX_LINES = 100;
const FIELD_NAME_MAX = 128;
const SIGN_IN_ID = /^csgn_[0-9a-f]{32}$/;

export const isCloudProviderKind = (value: unknown): value is CloudProviderKind =>
  typeof value === 'string' && (CLOUD_PROVIDER_KINDS as readonly string[]).includes(value);

export function validateCloudAccountId(value: unknown): string {
  if (typeof value !== 'string' || !ACCOUNT_ID.test(value))
    throw new Error('Choose a signed-in account.');
  return value;
}

export function validateCloudSignInId(value: unknown): string {
  if (typeof value !== 'string' || !SIGN_IN_ID.test(value))
    throw new Error('That sign-in is not known.');
  return value;
}

/** A bounded optional string from the renderer (picker place ids, search text, folder names). */
export function boundedCloudText(value: unknown, max: number, what: string): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value !== 'string' || value.length > max || /[\u0000-\u001f]/.test(value))
    throw new Error(`The ${what} is not valid.`);
  return value;
}

/** The only pages a sign-in may open in the browser: each provider's own sign-in host, over HTTPS. */
export const CLOUD_SIGN_IN_HOSTS = [
  'login.microsoftonline.com',
  'accounts.google.com',
  'www.dropbox.com',
] as const;

export function isCloudSignInUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 8_000) return false;
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      !url.port &&
      (CLOUD_SIGN_IN_HOSTS as readonly string[]).includes(url.hostname)
    );
  } catch {
    return false;
  }
}

/** A field map from the renderer: at most 100 lines of bounded names, each field once; "" targets leave a field out. */
export function cloudFieldMapInput(value: unknown): CloudFieldMapping[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value) || value.length > FIELD_MAP_MAX_LINES)
    throw new Error(`The field map may have at most ${FIELD_MAP_MAX_LINES} lines.`);
  const seen = new Set<string>();
  return value.map((line) => {
    const entry = line as Partial<CloudFieldMapping> | null;
    const field = typeof entry?.field === 'string' ? entry.field.trim() : '';
    const target = typeof entry?.target === 'string' ? entry.target.trim() : '';
    if (
      !field ||
      field.length > FIELD_NAME_MAX ||
      target.length > FIELD_NAME_MAX ||
      /[\u0000-\u001f]/.test(field + target)
    )
      throw new Error("Every field map line needs one of the job's fields.");
    if (seen.has(field.toLowerCase())) throw new Error(`The field "${field}" is mapped twice.`);
    seen.add(field.toLowerCase());
    return { field, target };
  });
}

export function cloudDestinationInput(value: unknown): CloudDestinationInput {
  const input = value as Partial<CloudDestinationInput> | null;
  if (!input || typeof input !== 'object') throw new Error('Choose where documents go.');
  const locationId = boundedCloudText(input.locationId, 2_048, 'folder');
  if (!locationId) throw new Error('Choose the folder documents go to.');
  const fieldMap = cloudFieldMapInput(input.fieldMap);
  return {
    accountId: validateCloudAccountId(input.accountId),
    locationId,
    locationPath: boundedCloudText(input.locationPath, 400, 'folder name') ?? '',
    sendValues: input.sendValues !== false,
    enabled: input.enabled !== false,
    ...(fieldMap.length > 0 ? { fieldMap } : {}),
  };
}
